import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, UserCircle, LogOut, Building2 } from 'lucide-react';
import RoleSwitcherSection from './RoleSwitcherSection';
import type { RoleSwitchControl } from '../../lib/roleSwitch';
import type { UserProfile } from '../../types';
import NotificationCenter from './NotificationCenter';
import TaskNotificationCenter from './TaskNotificationCenter';
import NotificationToggle from './NotificationToggle';
import ProfileModal from '../admin/ProfileModal';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import WorkspaceSettings from './WorkspaceSettings';
import { useWorkspaces } from './WorkspaceProvider';
import { hasModuleAccess, canSeeWorkspaceSettings as canSeeWorkspaceSettingsGate } from '../../lib/permissions';

interface UserMenuProps {
  profile?: UserProfile | null;
  email?: string;
  onLogout: () => void;
  /** Align the profile dropdown relative to the avatar (default: right). */
  align?: 'left' | 'right';
  /** Show the real-time notification bell next to the avatar (default: true). */
  showBell?: boolean;
  /** Called after the profile modal saves so parents can refresh profile data. */
  onProfileUpdated?: (updated: UserProfile) => void;
  /**
   * Role switching lives ONLY in this dropdown. When the user holds
   * more than one role, the switcher section lists them (current
   * marked, referrer "Pending" badge while its application is in
   * review) and switches in place.
   */
  roleSwitch?: RoleSwitchControl;
}

/**
 * UserMenu — top-right header cluster (notification bell + profile avatar with
 * dropdown). Portals the dropdown to document.body so the global header's
 * backdrop-filter stacking context can never trap it (see AGENTS.md dropdown
 * rule). Reused across every role dashboard so the bell + profile option is
 * consistent portal-wide.
 */
export default function UserMenu({
  profile,
  email,
  onLogout,
  align = 'right',
  showBell = true,
  onProfileUpdated,
  roleSwitch,
}: UserMenuProps) {
  const { activeWorkspace, platformAdmin, loading: workspacesLoading } = useWorkspaces();
  const activeRole = String(activeWorkspace?.role || '').toLowerCase();
  const isWorkspaceAdmin =
    (platformAdmin || ['admin', 'owner', 'super', 'master'].includes(activeRole)) &&
    hasModuleAccess('settings', profile, platformAdmin);
  // PR-10: isWorkspaceAdmin alone must NEVER unlock Backend Tasks.
  // Org approval (applyOrgAccountStatusChange → mutateUserRole) stamps
  // workspaceMembers[workspaceId] = 'admin' for every org owner (their
  // linkedUsers entry carries agentRole 'super'), so a freshly approved
  // referrer/employer/sponsor owner satisfies the workspace-admin check
  // in their own right — the internal Backend Tasks button rendered in
  // their top bar (it is NOT keyed on Enhanced Referrer Access), on the
  // dashboard AND in the wizard escape-hatch menu App floats over every
  // onboarding wizard. Two independent conditions, both required:
  //  (1) strictly POST-RESOLUTION — while workspace memberships are
  //      still loading, the button never renders (no permissive default
  //      during the unresolved window);
  //  (2) never for external org accounts — an account whose primary
  //      role is referrer/employer/sponsor is not internal staff, no
  //      matter what its workspace membership says: deny by default.
  // Staff accounts (profile role admin-tier, or team members whose
  // primary role is not an org role) are unaffected.
  const primaryRole = String(profile?.role || '').toLowerCase();
  const isExternalOrgAccount = ['referrer', 'employer', 'sponsor'].includes(primaryRole);
  const showBackendTasks = !workspacesLoading && isWorkspaceAdmin && !isExternalOrgAccount;
  // Workspace Settings visibility is decided by the single shared
  // predicate in src/lib/permissions.ts (canSeeWorkspaceSettings) so
  // every shell's profile menu — this one and the admin dashboard's own
  // dropdown — applies the identical deny-by-default rule: genuine
  // workspace admins and platform admins only, never external org
  // accounts, never in a non-admin session, never while memberships are
  // still resolving.
  const canSeeWorkspaceSettings = canSeeWorkspaceSettingsGate(profile, {
    platformAdmin,
    activeWorkspaceRole: activeWorkspace?.role,
    workspacesLoading,
  });
  const [open, setOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showWorkspaceSettings, setShowWorkspaceSettings] = useState(false);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const displayName =
    profile?.displayName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    email ||
    'User';
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
    || '?';

  const updatePos = () => {
    const rect = avatarRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      top: rect.bottom + 8,
      left: align === 'right' ? Math.max(8, rect.right - 260) : Math.max(8, rect.left),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    document.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      document.removeEventListener('scroll', updatePos, true);
    };
  }, [open, align]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideAvatar = avatarRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!insideAvatar && !insideDropdown) setOpen(false);
    };
    // Escape closes too (follow-up Item 8b audit — same close discipline
    // as the applicant dashboard's own profile menu).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const safeProfile: UserProfile = {
    uid: profile?.uid || '',
    email: email || profile?.email || '',
    role: profile?.role || 'applicant',
    createdAt: profile?.createdAt || new Date().toISOString(),
    ...(profile || {}),
  };

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {/* Backend Tasks is an internal admin operations tool (bulk jobs).
          It must NEVER render for external roles (referrer / employer /
          sponsor / applicant): UserMenu is shared across every role
          dashboard, so gate it on showBackendTasks — isWorkspaceAdmin
          MINUS external org accounts (see the PR-10 note above: org
          approval stamps org owners as workspace 'admin', so the bare
          workspace check leaks the button to every approved org owner).
          Not mounting it for non-admins also stops the component polling
          /api/admin/bulk-jobs every 15s. The bulk-jobs endpoints are
          additionally behind requireInternalAdmin on the server, which
          does not accept the workspace-admin fallback — so the API
          itself denies org accounts too, not just the UI. */}
      {showBell && showBackendTasks && <TaskNotificationCenter align="right" />}
      {showBell && <NotificationCenter align="right" />}
      <WorkspaceSwitcher />

      <div ref={avatarRef}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
        >
          <div className="h-8 w-8 rounded-full btn-primary flex items-center justify-center text-sm font-extrabold text-white shadow-sm overflow-hidden">
            {profile?.profilePhotoUrl ? (
              <img src={profile.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          role="menu"
          className="dropdown-portal w-64 max-w-[85vw] bg-white rounded-2xl shadow-2xl border border-slate-200 py-2"
          style={{ top: pos.top, left: pos.left, zIndex: 'var(--z-dropdown)' }}
        >
          {/* Profile header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full btn-primary flex items-center justify-center text-base font-extrabold text-white shadow-sm overflow-hidden shrink-0">
              {profile?.profilePhotoUrl ? (
                <img src={profile.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-slate-900 truncate">{displayName}</p>
              <p className="text-caption text-slate-400 truncate">{email}</p>
            </div>
          </div>

          {/* Notification toggle */}
          <div className="px-2 py-2 border-b border-slate-100">
            <NotificationToggle />
          </div>

          {/* My Profile */}
          <button
            onClick={() => { setOpen(false); setShowProfileModal(true); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
          >
            <UserCircle className="h-4 w-4 text-slate-400" />
            My Profile
          </button>

          {/* Workspace Settings — internal staff only. Never rendered
              for external org accounts (see canSeeWorkspaceSettings):
              org approval stamps them workspace 'admin' as a record of
              org ownership, which is not workspace adminship. */}
          {canSeeWorkspaceSettings && (
            <button
              onClick={() => { setOpen(false); setShowWorkspaceSettings(true); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
            >
              <Building2 className="h-4 w-4 text-slate-400" />
              Workspace Settings
            </button>
          )}

          {/* Switch Role — the single home of role switching */}
          {roleSwitch && (
            <RoleSwitcherSection
              options={roleSwitch.options}
              currentRoleId={roleSwitch.currentRoleId}
              loading={roleSwitch.loading}
              onSelectRole={(roleId) => { setOpen(false); roleSwitch.onSelectRole(roleId); }}
            />
          )}

          {/* Logout */}
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>,
        document.body
      )}

      {showProfileModal && (
        <ProfileModal
          profile={safeProfile}
          onClose={() => setShowProfileModal(false)}
          onProfileUpdated={(updated) => {
            setShowProfileModal(false);
            onProfileUpdated?.(updated);
          }}
          isAdminEditing={false}
        />
      )}

      {/* The settings modal is not even mounted for accounts that may
          never open it — no state path can surface the main workspace's
          settings to an external org account. */}
      {canSeeWorkspaceSettings && (
        <WorkspaceSettings open={showWorkspaceSettings} onClose={() => setShowWorkspaceSettings(false)} />
      )}
    </div>
  );
}
